import { useCallback, useEffect, useMemo, useState } from 'react'
import StatisticsView from './StatisticsView'
import { Center } from '../../model/domain/center'
import { Statistics } from '../../model/domain/statistics'
import TaskStatistics from '../../ws/TaskStatistics'

interface StatisticsContainerState {
    centers?: Center[]
    initialLoading: boolean
    centerId: number | null
    dateFrom: Date
    dateTo: Date
    loadingResults: boolean
    statistics?: Statistics[]
    error: string | null
}

const today = new Date()
const oneMonthAgo = new Date()
oneMonthAgo.setMonth(today.getMonth() - 1)

export default function StatisticsContainer() {
    const [state, setState] = useState<StatisticsContainerState>({
        initialLoading: false,
        centerId: null,
        dateFrom: oneMonthAgo,
        dateTo: today,
        loadingResults: false,
        error: null,
    })

    const updateState = (changes: Partial<StatisticsContainerState>) => {
        setState((state) => ({
            ...state,
            ...changes,
        }))
    }

    const task = useMemo(() => new TaskStatistics(), [])

    const fetchCenters = useCallback(async () => {
        try {
            updateState({ initialLoading: true })
            const response = await task.getCenters()
            const centers = response.data
            updateState({ centers })
        } catch (err) {
            if (err instanceof Error) {
                updateState({ error: err.message })
            }
        } finally {
            updateState({ initialLoading: false })
        }
    }, [task])

    useEffect(() => {
        fetchCenters()
    }, [fetchCenters])

    const {
        centers,
        initialLoading,
        centerId,
        dateFrom,
        dateTo,
        loadingResults,
        statistics,
        error,
    } = state

    const fetchStatistics = useCallback(async () => {
        try {
            updateState({ loadingResults: true, error: null })
            const statistics = await task.getStatistics(
                dateFrom,
                dateTo,
                centerId
            )
            updateState({ statistics })
        } catch (err) {
            if (err instanceof Error) {
                updateState({ error: err.message })
            }
        } finally {
            updateState({ loadingResults: false })
        }
    }, [task, centerId, dateFrom, dateTo])

    const handleChangeCenterId = (centerId: number | null) => {
        updateState({ centerId })
    }

    const handleChangeDateFrom = (dateFrom: string) => {
        updateState({ dateFrom: new Date(dateFrom) })
    }

    const handleChangeDateTo = (dateTo: string) => {
        updateState({ dateTo: new Date(dateTo) })
    }

    const handleFilter = () => {
        fetchStatistics()
    }

    return (
        <StatisticsView
            centers={centers}
            initialLoading={initialLoading}
            centerId={centerId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            loadingResults={loadingResults}
            statistics={statistics}
            error={error}
            onChangeCenterId={handleChangeCenterId}
            onChangeDateFrom={handleChangeDateFrom}
            onChangeDateTo={handleChangeDateTo}
            onFilter={handleFilter}
        />
    )
}
